import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePackingCases } from "@/hooks/usePackingGear";
import { PackingCase } from "@/types/packing";
import { Trash2, Edit, Plus } from "lucide-react";
import { CreateCaseDialog } from "./CreateCaseDialog";

interface CaseManagerDialogProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CaseManagerDialog({
  projectId,
  open,
  onOpenChange,
}: CaseManagerDialogProps) {
  const [editingCase, setEditingCase] = useState<PackingCase | null>(null);
  const [editName, setEditName] = useState("");
  const [deleteCase, setDeleteCase] = useState<PackingCase | null>(null);
  const [showCreateCase, setShowCreateCase] = useState(false);
  
  const { cases, updateCase, deleteCase: deleteCaseMutation } = usePackingCases(projectId);

  const handleEditCase = (caseItem: PackingCase) => {
    setEditingCase(caseItem);
    setEditName(caseItem.name);
  };

  const handleSaveEdit = async () => {
    if (!editingCase || !editName.trim()) return;

    try {
      await updateCase.mutateAsync({
        caseId: editingCase.id,
        updates: { name: editName.trim() }
      });
      setEditingCase(null);
      setEditName("");
    } catch (error) {
      // Error handled in mutation
    }
  };

  const handleDeleteCase = async () => {
    if (!deleteCase) return;

    try {
      await deleteCaseMutation.mutateAsync(deleteCase.id);
      setDeleteCase(null);
    } catch (error) {
      // Error handled in mutation
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              Manage Cases
              <Button onClick={() => setShowCreateCase(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Case
              </Button>
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-96 overflow-y-auto">
            {!cases || cases.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No cases created yet. Create your first case to start organizing gear.
              </div>
            ) : (
              cases.map((caseItem) => (
                <Card key={caseItem.id}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-4 h-4 rounded" 
                          style={{ backgroundColor: caseItem.color }}
                        />
                        {editingCase?.id === caseItem.id ? (
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') handleSaveEdit();
                              if (e.key === 'Escape') {
                                setEditingCase(null);
                                setEditName("");
                              }
                            }}
                            className="w-48"
                            autoFocus
                          />
                        ) : (
                          <div>
                            <h4 className="font-medium">{caseItem.name}</h4>
                            {caseItem.description && (
                              <p className="text-sm text-muted-foreground">{caseItem.description}</p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {editingCase?.id === caseItem.id ? (
                          <>
                            <Button size="sm" onClick={handleSaveEdit} disabled={updateCase.isPending}>
                              Save
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              onClick={() => {
                                setEditingCase(null);
                                setEditName("");
                              }}
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleEditCase(caseItem)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => setDeleteCase(caseItem)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      <CreateCaseDialog
        projectId={projectId}
        open={showCreateCase}
        onOpenChange={setShowCreateCase}
      />

      <AlertDialog open={!!deleteCase} onOpenChange={() => setDeleteCase(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Case</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteCase?.name}"? Items assigned to this case will become unassigned. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteCase} disabled={deleteCaseMutation.isPending}>
              {deleteCaseMutation.isPending ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}